import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { apiClient, ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import type { StorageDetailResponse, StorageDeleteResponse, StorageDeletePreviewResponse } from "@/types/api";
import { useDeleteStorage } from "./hooks/useStorages";

type Action = StorageDeletePreviewResponse["deleteAction"];
const forceAction = (action: Action) => action.startsWith("force_");
const routesAction = (action: Action) => action.endsWith("_with_local_gateway_routes_delete");

export function StorageDeleteControl({ storage, onDeleted }: {
  storage: Pick<StorageDetailResponse, "storageId" | "displayName" | "deleteCapabilities">;
  onDeleted: (gatewayCleanupNeeded: boolean) => void;
}) {
  const { t } = useTranslation("storages");
  const deletion = useDeleteStorage(storage.storageId);
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [preview, setPreview] = useState<StorageDeletePreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<StorageDeleteResponse | null>(null);
  const [confirmedKind, setConfirmedKind] = useState<"normal" | "force" | null>(null);
  // Prevent duplicate submission before React has committed the loading state.
  const inFlight = useRef(false);
  const canDelete = storage.deleteCapabilities.normalDeleteAllowed || storage.deleteCapabilities.forceDeleteUnusedAllowed;
  const disabledReason = t(storage.deleteCapabilities.unusedStorage
    ? "detail.deleteDisabledNoPermission" : "detail.deleteDisabledUsed");
  const kind = preview && forceAction(preview.deleteAction) ? "force" : "normal";
  const describeError = (failure: unknown) => {
    const code = failure instanceof ApiError ? failure.code : "generic";
    const message = t(`detail.deleteErrors.${code}`, { defaultValue: t("detail.deleteErrors.generic") });
    return failure instanceof ApiError && failure.requestId
      ? `${message}（${t("delete.requestId")}: ${failure.requestId}）` : message;
  };
  const inspect = async () => {
    setChecking(true);
    setError(null);
    deletion.reset();
    try {
      const value = await apiClient<StorageDeletePreviewResponse>(`/storages/${storage.storageId}/delete-preview`);
      if (!value || value.storageId !== storage.storageId || ![
        "normal_delete", "force_delete_unused", "confirm_delete_with_local_gateway_routes_delete",
        "force_delete_unused_with_local_gateway_routes_delete", "force_delete_broken_reference",
      ].includes(value.deleteAction) || !Number.isInteger(value.activeLocalGatewayRouteCount) || value.activeLocalGatewayRouteCount < 0) {
        throw new Error("Invalid deletion preview");
      }
      setPreview(value);
    } catch (failure) {
      setPreview(null);
      setError(describeError(failure));
    } finally { setChecking(false); }
  };
  const start = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setOpen(true);
    // A pending command keeps its action and idempotency key even after closing the dialog.
    if (!pending) {
      setConfirmedKind(null);
      setPreview(null);
      await inspect();
    }
    inFlight.current = false;
  };
  const confirm = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      if (!preview || error) { await inspect(); return; }
      setConfirmedKind(kind);
      setError(null);
      deletion.reset();
      try {
        const result = await deletion.mutate({ deleteAction: preview.deleteAction });
        if (result.deleted) { setOpen(false); onDeleted(routesAction(preview.deleteAction)); }
        else setPending(result);
      } catch (failure) {
        deletion.reset();
        if (failure instanceof ApiError && failure.code === "storage_delete_broken_reference_confirmation_required") {
          // A different deletion method requires a fresh name confirmation.
          setPreview({ ...preview, deleteAction: "force_delete_broken_reference", activeLocalGatewayRouteCount: 0 });
          setConfirmedKind(null);
        } else if (failure instanceof ApiError && failure.code === "storage_delete_local_gateway_routes_confirmation_required") {
          // Conditions changed after preview: explain route removal, without a second text entry.
          setConfirmedKind(storage.deleteCapabilities.normalDeleteAllowed ? "normal" : "force");
          setPreview({ ...preview, deleteAction: storage.deleteCapabilities.normalDeleteAllowed
            ? "confirm_delete_with_local_gateway_routes_delete" : "force_delete_unused_with_local_gateway_routes_delete" });
        } else setError(describeError(failure));
      }
    } finally { inFlight.current = false; }
  };
  const title = pending ? t("delete.pendingTitle")
    : preview?.deleteAction === "force_delete_broken_reference" ? t("delete.brokenReferenceTitle")
      : preview && routesAction(preview.deleteAction) ? t("delete.localGatewayRoutesDeleteTitle") : t("delete.title");
  const deleteButton = <Button variant="destructive" size="sm" onClick={() => void start()} disabled={!canDelete}>
    <Trash2 className="mr-2 h-4 w-4" />{t("detail.deleteCta")}
  </Button>;
  return <>
    {canDelete ? deleteButton : (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0} aria-label={t("detail.deleteCta")} className="inline-flex cursor-help rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
              {deleteButton}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-left" side="bottom" align="end">
            {disabledReason}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )}
    <ConfirmDialog
      key={preview?.deleteAction === "force_delete_broken_reference" ? "broken" : "regular"}
      open={open}
      onClose={() => { setOpen(false); setError(null); deletion.reset(); }}
      onConfirm={confirm}
      title={title}
      description={<div className="space-y-2">
        {checking ? <p>{t("delete.checking")}</p> : null}
        {error ? <p role="alert" className="text-destructive">{error}</p> : null}
        {pending ? <p role="status">{t("delete.pendingDescription", { operationId: pending.serviceOperationId })}</p> : null}
        {!pending && preview ? <>
          {forceAction(preview.deleteAction) ? <p>{t(preview.deleteAction === "force_delete_broken_reference" ? "delete.forceWarning" : "delete.adminForceWarning")}</p> : null}
          {preview.deleteAction === "force_delete_broken_reference" ? <>
            <p>{t("delete.brokenReferenceDescription")}</p><p>{t("delete.brokenReferenceScope")}</p>
          </> : <p>{t("delete.description")}</p>}
          <p>{t("delete.filesRetained")}</p>
          {routesAction(preview.deleteAction) ? <>
            <p>{t(preview.activeLocalGatewayRouteCount > 0 ? "delete.localGatewayRoutesDeleteDescription" : "delete.routesChangedDescription", { count: preview.activeLocalGatewayRouteCount })}</p>
          </> : null}
        </> : null}
      </div>}
      matchValue={preview && !error && !pending && confirmedKind !== kind ? storage.displayName : undefined}
      matchLabel={t("delete.confirmLabel")}
      isLoading={checking || deletion.isLoading}
      variant="destructive"
      confirmText={error || !preview ? t("delete.recheck") : pending ? t("delete.retryPending")
        : forceAction(preview.deleteAction) ? t("delete.forceCta") : t("detail.deleteCta")}
    />
  </>;
}
